import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Remembers each filter accordion's user-set open/closed state across section re-renders (morph),
// keyed by the filter's stable param name. A module-level Map is used deliberately: it survives
// even when morph *replaces* an accordion element (a freshly inserted element never receives the
// old element's attributes, so attribute-based tracking would be lost), yet it resets on a full
// page load because the script re-executes — so a refresh falls back to the theme-settings default.
// Only filter accordions participate: those are the ones that carry `data-filter-param-name`.
const filterOpenState = new Map();

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  /**
   * The key used to persist this accordion's open state for the session. Only filter accordions
   * carry `data-filter-param-name`, so non-filter accordions return null and keep the default
   * (theme-settings-driven) behaviour untouched.
   * @returns {string|null}
   */
  get #stateKey() {
    return this.dataset.filterParamName ?? null;
  }

  #controller = new AbortController();

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // If the shopper has opened/closed this filter during the session, restore that state (it
    // survives section re-renders, including element replacement). Otherwise — and on a fresh
    // page load, when the store is empty — fall back to the theme-settings default.
    const key = this.#stateKey;
    if (key !== null && filterOpenState.has(key)) {
      this.details.open = /** @type {boolean} */ (filterOpenState.get(key));
    } else {
      this.#setDefaultOpenState();
    }

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Closes all sibling accordion rows when this one opens, if the parent
   * container has the `data-exclusive` attribute.
   */
  #handleToggle = () => {
    // Remember the shopper's chosen state so section re-renders can restore it.
    const key = this.#stateKey;
    if (key !== null) filterOpenState.set(key, this.details.open);

    if (!this.details.open) return;

    const container = this.closest('[data-exclusive]');
    if (!container) return;

    container.querySelectorAll('accordion-custom').forEach((accordion) => {
      if (accordion === this) return;
      if (this.contains(accordion)) return;
      if (accordion.closest('[data-exclusive]') !== container) return;
      accordion.close();
    });
  };

  /**
   * Closes this accordion.
   *
   * @public
   */
  close() {
    this.details.open = false;
  }

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    // Keep a shopper-set filter state across a breakpoint change (e.g. opened a filter on mobile
    // then resized to desktop); otherwise reset to the theme default.
    const key = this.#stateKey;
    if (key !== null && filterOpenState.has(key)) {
      this.details.open = /** @type {boolean} */ (filterOpenState.get(key));
    } else {
      this.#setDefaultOpenState();
    }
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.details.open = false;
    }
  };
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
